import { useState, useEffect, useMemo, useCallback } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  DragEndEvent,
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import {
  getLoginPhotos,
  deleteLoginPhoto,
  toggleLoginPhotoActive,
  reorderLoginPhotos,
} from '@/api/login-photos';
import { getThemes } from '@/api/themes';
import type { LoginPhoto, LoginPhotoFilters, Theme } from '@/types/login-photo';
import {
  Plus,
  Search,
  Image,
  Edit,
  Trash2,
  MoreHorizontal,
  Eye,
  EyeOff,
  GripVertical,
  Calendar,
  Tag,
  Settings,
  CalendarDays,
  ArrowDown,
  ArrowUpDown,
} from 'lucide-react';
import { ThemeManager } from '@/components/login-photos/ThemeManager';

interface SortableRowProps {
  photo: LoginPhoto;
  onEdit: (id: number) => void;
  onDelete: (id: number) => void;
  onToggleActive: (id: number) => void;
}

function SortableRow({ photo, onEdit, onDelete, onToggleActive }: SortableRowProps) {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: photo.id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  const formatDate = (dateStr: string | null) => {
    if (!dateStr) return '-';
    const date = new Date(dateStr);
    return date.toLocaleDateString('fr-FR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
    });
  };

  const formatDates = (dates: string[]) => {
    if (dates.length === 0) return '-';
    const today = new Date().toISOString().split('T')[0];
    const futureDates = dates.filter(d => d >= today).slice(0, 3);
    if (futureDates.length === 0) return 'Aucune date';
    return futureDates.map(d => formatDate(d)).join(', ') + (dates.filter(d => d >= today).length > 3 ? '...' : '');
  };

  return (
    <tr
      ref={setNodeRef}
      style={style}
      className={`border-b hover:bg-muted/50 ${!photo.isActive ? 'opacity-60 bg-gray-50' : ''}`}
    >
      <td className="p-3 w-10">
        <button
          {...attributes}
          {...listeners}
          className="cursor-grab hover:bg-gray-200 rounded p-1"
        >
          <GripVertical className="h-4 w-4 text-gray-400" />
        </button>
      </td>
      <td className="p-3">
        <div className="text-sm">
          {photo.forcedDate ? (
            <Badge variant="secondary" className="text-xs">
              <Calendar className="h-3 w-3 mr-1" />
              {formatDate(photo.forcedDate)}
            </Badge>
          ) : (
            <span className="text-muted-foreground text-xs">{formatDates(photo.displayDates)}</span>
          )}
        </div>
      </td>
      <td className="p-3 w-20">
        {photo.thumbnailUrl ? (
          <button onClick={() => onEdit(photo.id)} className="block">
            <img
              src={photo.thumbnailUrl}
              alt={photo.title}
              className="w-16 h-12 object-cover rounded border hover:opacity-80 transition-opacity cursor-pointer"
            />
          </button>
        ) : (
          <div className="w-16 h-12 bg-gray-100 rounded border flex items-center justify-center">
            <Image className="h-5 w-5 text-gray-400" />
          </div>
        )}
      </td>
      <td className="p-3">
        <button onClick={() => onEdit(photo.id)} className="text-left hover:text-primary transition-colors cursor-pointer">
          <div className="font-medium">{photo.title}</div>
        </button>
        {photo.comment && (
          <div className="text-xs text-muted-foreground truncate max-w-xs">
            {photo.comment}
          </div>
        )}
      </td>
      <td className="p-3">
        {photo.theme ? (
          <Badge variant="outline" className="text-xs">
            <Tag className="h-3 w-3 mr-1" />
            {photo.theme.name}
          </Badge>
        ) : (
          <span className="text-xs text-muted-foreground">-</span>
        )}
      </td>
      <td className="p-3">
        <Badge variant={photo.isActive ? 'success' : 'secondary'}>
          {photo.isActive ? 'Actif' : 'Inactif'}
        </Badge>
      </td>
      <td className="p-3 text-right">
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button variant="ghost" size="sm">
              <MoreHorizontal className="h-4 w-4" />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end">
            <DropdownMenuItem onClick={() => onEdit(photo.id)}>
              <Edit className="h-4 w-4 mr-2" />
              Modifier
            </DropdownMenuItem>
            <DropdownMenuItem onClick={() => onToggleActive(photo.id)}>
              {photo.isActive ? (
                <>
                  <EyeOff className="h-4 w-4 mr-2" />
                  Desactiver
                </>
              ) : (
                <>
                  <Eye className="h-4 w-4 mr-2" />
                  Activer
                </>
              )}
            </DropdownMenuItem>
            <DropdownMenuItem
              onClick={() => onDelete(photo.id)}
              className="text-red-600"
            >
              <Trash2 className="h-4 w-4 mr-2" />
              Supprimer
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </td>
    </tr>
  );
}

export function LoginPhotosPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [photos, setPhotos] = useState<LoginPhoto[]>([]);
  const [themes, setThemes] = useState<Theme[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [totalItems, setTotalItems] = useState(0);
  const [totalPages, setTotalPages] = useState(1);
  const [themeManagerOpen, setThemeManagerOpen] = useState(false);

  // Get filters from URL params
  const filters: LoginPhotoFilters = useMemo(
    () => ({
      search: searchParams.get('search') || '',
      theme: searchParams.get('theme') || '',
      isActive: searchParams.get('isActive') || '',
      page: Number(searchParams.get('page')) || 1,
      limit: Number(searchParams.get('limit')) || 25,
      sortBy: searchParams.get('sortBy') || 'forcedDate',
      sortOrder: 'DESC' as const,
    }),
    [searchParams]
  );

  // Update filters
  const updateFilters = useCallback(
    (newFilters: Partial<LoginPhotoFilters>) => {
      const updated = { ...filters, ...newFilters };
      const params = new URLSearchParams();

      if (updated.search) params.set('search', updated.search);
      if (updated.theme) params.set('theme', String(updated.theme));
      if (updated.isActive !== '' && updated.isActive !== null && updated.isActive !== undefined) {
        params.set('isActive', String(updated.isActive));
      }
      if (updated.page && updated.page !== 1) params.set('page', updated.page.toString());
      if (updated.limit && updated.limit !== 25) params.set('limit', updated.limit.toString());
      if (updated.sortBy && updated.sortBy !== 'forcedDate') params.set('sortBy', updated.sortBy);

      setSearchParams(params);
    },
    [filters, setSearchParams]
  );

  // Fetch data
  const fetchData = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [photosResponse, themesResponse] = await Promise.all([
        getLoginPhotos(filters),
        getThemes(),
      ]);

      setPhotos(photosResponse.data);
      setTotalItems(photosResponse.meta.total);
      setTotalPages(photosResponse.meta.totalPages);
      setThemes(themesResponse);
    } catch (err) {
      setError('Erreur lors du chargement des photos');
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, [filters]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // Handle search with debounce
  const [searchInput, setSearchInput] = useState(filters.search || '');

  useEffect(() => {
    const timer = setTimeout(() => {
      if (searchInput !== filters.search) {
        updateFilters({ search: searchInput, page: 1 });
      }
    }, 300);

    return () => clearTimeout(timer);
  }, [searchInput, filters.search, updateFilters]);

  // Drag and drop
  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  const handleDragEnd = async (event: DragEndEvent) => {
    const { active, over } = event;

    if (over && active.id !== over.id) {
      const oldIndex = photos.findIndex((p) => p.id === active.id);
      const newIndex = photos.findIndex((p) => p.id === over.id);

      const activePhoto = photos[oldIndex];
      const overPhoto = photos[newIndex];

      const newPhotos = arrayMove(photos, oldIndex, newIndex);
      setPhotos(newPhotos);

      try {
        // Swap dates between the two photos
        const swapDateIds: [number, number] = [activePhoto.id, overPhoto.id];
        await reorderLoginPhotos(newPhotos.map((p) => p.id), swapDateIds);
        // Refresh to get updated dates
        fetchData();
      } catch (err) {
        console.error('Error reordering:', err);
        fetchData();
      }
    }
  };

  // Actions
  const handleEdit = (id: number) => {
    window.location.href = `/login-photos/${id}/edit`;
  };

  const handleDelete = async (id: number) => {
    if (!confirm('Etes-vous sur de vouloir supprimer cette photo ?')) return;

    try {
      await deleteLoginPhoto(id);
      fetchData();
    } catch (err) {
      console.error('Error deleting:', err);
      setError('Erreur lors de la suppression');
    }
  };

  const handleToggleActive = async (id: number) => {
    try {
      await toggleLoginPhotoActive(id);
      setPhotos((prev) =>
        prev.map((p) => (p.id === id ? { ...p, isActive: !p.isActive } : p))
      );
    } catch (err) {
      console.error('Error toggling active:', err);
      setError('Erreur lors du changement de statut');
    }
  };

  const handleSort = (column: string) => {
    updateFilters({ sortBy: column });
  };

  const SortIcon = ({ column }: { column: string }) => {
    if (filters.sortBy !== column) return <ArrowUpDown className="h-3 w-3 ml-1 opacity-40" />;
    return <ArrowDown className="h-3 w-3 ml-1" />;
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div className="space-y-2">
          <div className="flex items-center gap-3">
            <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-primary/10 text-primary">
              <Image className="h-5 w-5" />
            </div>
            <h1 className="text-2xl md:text-3xl font-bold tracking-tight text-foreground">
              Photos du login
            </h1>
          </div>
          <p className="text-sm md:text-base text-muted-foreground pl-[52px]">
            Gerez les photos affichees sur la page de connexion.
          </p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" asChild>
            <Link to="/login-photos/calendar">
              <CalendarDays className="h-4 w-4 mr-2" />
              Calendrier
            </Link>
          </Button>
          <Button variant="outline" onClick={() => setThemeManagerOpen(true)}>
            <Settings className="h-4 w-4 mr-2" />
            Themes
          </Button>
          <Button asChild>
            <Link to="/login-photos/new">
              <Plus className="h-4 w-4 mr-2" />
              Ajouter
            </Link>
          </Button>
        </div>
      </div>

      {/* Error */}
      {error && (
        <div className="rounded-lg bg-red-50 border border-red-200 p-4 text-red-700">
          {error}
          <button
            onClick={() => setError(null)}
            className="ml-2 text-red-500 hover:text-red-700"
          >
            &times;
          </button>
        </div>
      )}

      {/* Filters */}
      <div className="flex flex-wrap items-center gap-4">
        <div className="relative flex-1 min-w-[200px] max-w-sm">
          <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
          <Input
            placeholder="Rechercher..."
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
            className="pl-9"
          />
        </div>

        <Select
          value={filters.theme?.toString() || 'all'}
          onValueChange={(value) =>
            updateFilters({ theme: value === 'all' ? '' : value, page: 1 })
          }
        >
          <SelectTrigger className="w-[180px]">
            <SelectValue placeholder="Theme" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Tous les themes</SelectItem>
            <SelectItem value="null">Sans theme</SelectItem>
            {themes.map((theme) => (
              <SelectItem key={theme.id} value={theme.id.toString()}>
                {theme.name} ({theme.photoCount})
              </SelectItem>
            ))}
          </SelectContent>
        </Select>

        <Select
          value={filters.isActive?.toString() || 'all'}
          onValueChange={(value) =>
            updateFilters({ isActive: value === 'all' ? '' : value, page: 1 })
          }
        >
          <SelectTrigger className="w-[150px]">
            <SelectValue placeholder="Statut" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Tous</SelectItem>
            <SelectItem value="true">Actifs</SelectItem>
            <SelectItem value="false">Inactifs</SelectItem>
          </SelectContent>
        </Select>

        <span className="text-sm text-muted-foreground ml-auto">
          {totalItems} photo{totalItems > 1 ? 's' : ''}
        </span>
      </div>

      {/* Table */}
      <div className="rounded-lg border bg-card overflow-hidden">
        {loading ? (
          <div className="p-8 text-center text-muted-foreground">Chargement...</div>
        ) : photos.length === 0 ? (
          <div className="p-8 text-center text-muted-foreground">
            Aucune photo trouvee.
          </div>
        ) : (
          <DndContext
            sensors={sensors}
            collisionDetection={closestCenter}
            onDragEnd={handleDragEnd}
          >
            <table className="w-full">
              <thead className="bg-muted/50">
                <tr className="border-b">
                  <th className="p-3 w-10"></th>
                  <th
                    className="p-3 text-left text-xs font-medium text-muted-foreground cursor-pointer hover:text-foreground select-none"
                    onClick={() => handleSort('forcedDate')}
                  >
                    <span className="inline-flex items-center">Dates<SortIcon column="forcedDate" /></span>
                  </th>
                  <th className="p-3 w-20 text-left text-xs font-medium text-muted-foreground">
                    Photo
                  </th>
                  <th
                    className="p-3 text-left text-xs font-medium text-muted-foreground cursor-pointer hover:text-foreground select-none"
                    onClick={() => handleSort('title')}
                  >
                    <span className="inline-flex items-center">Titre<SortIcon column="title" /></span>
                  </th>
                  <th
                    className="p-3 text-left text-xs font-medium text-muted-foreground cursor-pointer hover:text-foreground select-none"
                    onClick={() => handleSort('theme')}
                  >
                    <span className="inline-flex items-center">Theme<SortIcon column="theme" /></span>
                  </th>
                  <th
                    className="p-3 text-left text-xs font-medium text-muted-foreground cursor-pointer hover:text-foreground select-none"
                    onClick={() => handleSort('isActive')}
                  >
                    <span className="inline-flex items-center">Statut<SortIcon column="isActive" /></span>
                  </th>
                  <th className="p-3 w-10"></th>
                </tr>
              </thead>
              <tbody>
                <SortableContext
                  items={photos.map((p) => p.id)}
                  strategy={verticalListSortingStrategy}
                >
                  {photos.map((photo) => (
                    <SortableRow
                      key={photo.id}
                      photo={photo}
                      onEdit={handleEdit}
                      onDelete={handleDelete}
                      onToggleActive={handleToggleActive}
                    />
                  ))}
                </SortableContext>
              </tbody>
            </table>
          </DndContext>
        )}
      </div>

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <span className="text-sm text-muted-foreground">Afficher</span>
            <Select
              value={filters.limit.toString()}
              onValueChange={(value) =>
                updateFilters({ limit: Number(value), page: 1 })
              }
            >
              <SelectTrigger className="w-[80px]">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="10">10</SelectItem>
                <SelectItem value="25">25</SelectItem>
                <SelectItem value="50">50</SelectItem>
                <SelectItem value="100">100</SelectItem>
              </SelectContent>
            </Select>
          </div>

          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              size="sm"
              disabled={filters.page <= 1}
              onClick={() => updateFilters({ page: filters.page - 1 })}
            >
              Precedent
            </Button>
            <span className="text-sm text-muted-foreground">
              Page {filters.page} sur {totalPages}
            </span>
            <Button
              variant="outline"
              size="sm"
              disabled={filters.page >= totalPages}
              onClick={() => updateFilters({ page: filters.page + 1 })}
            >
              Suivant
            </Button>
          </div>
        </div>
      )}

      {/* Theme Manager Modal */}
      <ThemeManager
        open={themeManagerOpen}
        onClose={() => setThemeManagerOpen(false)}
        onUpdate={fetchData}
      />
    </div>
  );
}
